import React, { useState, useEffect, useRef } from 'react'
import VisitsService from '../../../services/VisitsService'

const DashboardCounts = ({
  dateFilter = 'year',
  filters = {},
  countsRefreshKey = 0,
  onVisitCountsClick,
  onDeficientChartingClick,
  onReviewOrdersClick,
  onMissingClaimNumbersClick,
  canViewVisitCountsEyeIcon = true,
  canViewDeficientChartingEyeIcon = true,
  canViewOrdersCountEyeIcon = true,
  canViewMissingClaimNumbersEyeIcon = true
}) => {
  const [visitCounts, setVisitCounts] = useState({
    scheduled: 0,
    pending: 0,
    completed: 0,
    total: 0
  })
  const [billingHoldCounts, setBillingHoldCounts] = useState({
    today: 0,
    less_than_7_days: 0,
    more_than_8_days: 0,
    total: 0
  })
  const [ordersCounts, setOrdersCounts] = useState({
    pending: 0,
    ready_to_review: 0,
    completed: 0,
    total: 0
  })
  const [missingClaimCounts, setMissingClaimCounts] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    failed: 0
  })
  // const [averageLos, setAverageLos] = useState({
  //   employer_services: 0,
  //   work_comp: 0,
  //   private: 0
  // })
  const [loading, setLoading] = useState(false)
  const visitCountsCardRef = useRef(null)
  const deficientChartingCardRef = useRef(null)
  const ordersCardRef = useRef(null)
  const missingClaimCardRef = useRef(null)

  const normalizeMissingClaimCounts = (rawCounts = {}) => {
    const completed = Number(rawCounts?.completed) || 0
    const pending = Number(rawCounts?.pending) || 0
    const failed = Number(rawCounts?.failed) || 0
    const total =
      Number(rawCounts?.total) ||
      Number(rawCounts?.total_missing_claims) ||
      (completed + pending + failed)

    return {
      total,
      completed,
      pending,
      failed
    }
  }

  const getRectFromRef = (ref) => {
    if (!ref?.current) return null
    const rect = ref.current.getBoundingClientRect()
    return { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
  }

  useEffect(() => {
    fetchVisitCounts()
  }, [dateFilter, filters, countsRefreshKey])

  const fetchVisitCounts = async () => {
    setLoading(true)
    try {
      const payload = {
        date_filter: dateFilter || 'year',
        ...filters
      }
      const [response, dashboardCountsResponse] = await Promise.all([
        VisitsService.getNewDashboardCounts(payload),
        VisitsService.getDashboardCounts(payload)
      ])
      if (response && response.visit_counts) {
        setVisitCounts(response.visit_counts)
      }
      if (response && response.billing_hold_counts) {
        setBillingHoldCounts(response.billing_hold_counts)
      }
      if (response && response.orders_counts) {
        setOrdersCounts(response.orders_counts)
      }
      const missingClaimData =
        response?.missing_claim_numbers ||
        dashboardCountsResponse?.missing_claim_numbers ||
        {}
      setMissingClaimCounts(normalizeMissingClaimCounts(missingClaimData))
      // if (response && response.average_los) {
      //   setAverageLos(response.average_los)
      // }
    } catch (error) {
      console.error('Error fetching visit counts:', error)
    } finally {
      setLoading(false)
    }
  }

  const calculateDonutChart = (scheduled, pending, completed) => {
    const total = scheduled + pending + completed
    const circumference = 2 * Math.PI * 65 // radius = 65
    
    if (total === 0) {
      // When all values are 0, show equal segments (1/3 each)
      const equalSegment = circumference / 3
      return {
        completed: equalSegment,
        pending: equalSegment,
        scheduled: equalSegment,
        circumference: circumference
      }
    }

    const completedPercent = (completed / total) * circumference
    const pendingPercent = (pending / total) * circumference
    const scheduledPercent = (scheduled / total) * circumference

    return {
      completed: completedPercent,
      pending: pendingPercent,
      scheduled: scheduledPercent,
      circumference: circumference
    }
  }

  const calculateBillingHoldDonutChart = (today, lessThan7, moreThan8) => {
    const total = today + lessThan7 + moreThan8
    const circumference = 2 * Math.PI * 65 // radius = 65
    
    if (total === 0) {
      // When all values are 0, show equal segments (1/3 each)
      const equalSegment = circumference / 3
      return {
        today: equalSegment,
        lessThan7: equalSegment,
        moreThan8: equalSegment,
        circumference: circumference
      }
    }

    const todayPercent = (today / total) * circumference
    const lessThan7Percent = (lessThan7 / total) * circumference
    const moreThan8Percent = (moreThan8 / total) * circumference

    return {
      today: todayPercent,
      lessThan7: lessThan7Percent,
      moreThan8: moreThan8Percent,
      circumference: circumference
    }
  }

  const donutData = calculateDonutChart(
    visitCounts.scheduled,
    visitCounts.pending,
    visitCounts.completed
  )

  const billingHoldDonutData = calculateBillingHoldDonutChart(
    billingHoldCounts.today,
    billingHoldCounts.less_than_7_days,
    billingHoldCounts.more_than_8_days
  )

  // For orders: pending maps to scheduled position (red), ready_to_review maps to pending position (yellow), completed maps to completed position (green)
  const ordersDonutData = calculateDonutChart(
    ordersCounts.pending,      // maps to scheduled (red, seg3)
    ordersCounts.ready_to_review, // maps to pending (yellow, seg2)
    ordersCounts.completed     // maps to completed (green, seg1)
  )

  const calculateMissingClaimsDonutChart = (completed, pending, failed) => {
    const total = completed + pending + failed
    const circumference = 2 * Math.PI * 65 // radius = 65
    
    if (total === 0) {
      // When all values are 0, show equal segments (1/3 each)
      const equalSegment = circumference / 3
      return {
        completed: equalSegment,
        pending: equalSegment,
        failed: equalSegment,
        circumference: circumference
      }
    }

    const completedPercent = (completed / total) * circumference
    const pendingPercent = (pending / total) * circumference
    const failedPercent = (failed / total) * circumference

    return {
      completed: completedPercent,
      pending: pendingPercent,
      failed: failedPercent,
      circumference: circumference
    }
  }

  const missingClaimDonutData = calculateMissingClaimsDonutChart(
    missingClaimCounts.completed,
    missingClaimCounts.pending,
    missingClaimCounts.failed
  )
  // const losDonutData = calculateLosDonutChart(
  //   averageLos.employer_services,
  //   averageLos.work_comp,
  //   averageLos.private
  // )
  const EyeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.5 8C12.5 11.5 10.5 13 8 13C5.5 13 3.5 11.5 1.5 8C3.5 4.5 5.5 3 8 3C10.5 3 12.5 4.5 14.5 8Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  const ColorDot = ({ color }) => (
    <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect y="0.5" width="12" height="12" rx="6" fill={color}></rect>
    </svg>
  )

  return (
    <div className="row">
      {/* Visit Counts */}
      <div ref={visitCountsCardRef} className="col-lg-3 col-md-6 col-12 mb-2 pb-1">
        <div className="w-100 sh_statusIndicatorbox blue-orichid p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="p1-strong mb-2">Visit Counts</h1>
            {canViewVisitCountsEyeIcon && onVisitCountsClick && (
              <div>
                <a className="btn sh_btniconnew" onClick={() => { const rect = getRectFromRef(visitCountsCardRef); if (rect) onVisitCountsClick(rect); }} style={{ cursor: 'pointer' }} title="View overall visits">
                  <EyeIcon />
                </a>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center">
            <div className=" d-flex justify-content-center pr-3">
              <svg id="donutSvg" width="80" height="80" viewBox="0 0 136 136" strokeWidth="6">
                <circle className="bg" cx="68" cy="68" r="65" stroke="#f2f2f2" fill="none"></circle>
                <circle
                  id="seg1"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#0DAC59"
                  fill="none"
                  strokeDasharray={`${donutData.completed} ${donutData.circumference - donutData.completed}`}
                  strokeDashoffset="0"
                  strokeLinecap="round"
                ></circle>
                <circle
                  id="seg2"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#F6447E"
                  fill="none"
                  strokeDasharray={`${donutData.pending} ${donutData.circumference - donutData.pending}`}
                  strokeDashoffset={-donutData.completed}
                  strokeLinecap="round"
                ></circle>
                <circle
                  id="seg3"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#7D4BC5"
                  fill="none"
                  strokeDasharray={`${donutData.scheduled} ${donutData.circumference - donutData.scheduled}`}
                  strokeDashoffset={-(donutData.completed + donutData.pending)}
                  strokeLinecap="round"
                ></circle>
                <g className="iconGroup" fill="transparent" transform="translate(50 50) scale(1.2)">
                  <path
                    d="M18.831 13.1732C20.3923 14.7346 20.3923 17.2692 18.831 18.8332C17.2697 20.3946 14.735 20.3946 13.171 18.8332C11.6097 17.2719 11.6097 14.7372 13.171 13.1732C14.735 11.6092 17.2683 11.6092 18.831 13.1732"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M27.9987 21.3333L23.9987 25.3333L21.332 22.6666"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 25.3334C11.0787 25.3334 6.61467 22.6787 4.59467 18.5494V18.5494C4.20267 17.7481 4 16.8787 4 16.0001C4 15.1214 4.20267 14.2521 4.59467 13.4507V13.4507C6.61467 9.32141 11.0787 6.66675 16 6.66675C20.9213 6.66675 25.3853 9.32141 27.4053 13.4507V13.4507C27.7973 14.2521 28 15.1214 28 16.0001"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>
            </div>
            <div className="d-flex flex-column w-100">
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#7D4BC5" />
                  </span>
                  Scheduled{' '}
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : visitCounts.scheduled}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#F6447E" />
                  </span>
                  Pending
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : visitCounts.pending}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#0DAC59" />
                  </span>
                  Completed
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : visitCounts.completed}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts- Incomplete / Billing Hold */}
      <div ref={deficientChartingCardRef} className="col-lg-3 col-md-6 col-12 mb-2 pb-1">
        <div className="w-100 sh_statusIndicatorbox pale-purple p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="p1-strong mb-2">Charts- Incomplete / Billing Hold</h1>
            {canViewDeficientChartingEyeIcon && onDeficientChartingClick && (
              <div>
                <a className="btn sh_btniconnew" onClick={() => { const rect = getRectFromRef(deficientChartingCardRef); if (rect) onDeficientChartingClick(rect); }} style={{ cursor: 'pointer' }} title="View Deficient Charting / Billing Holds">
                  <EyeIcon />
                </a>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center">
            <div className=" d-flex justify-content-center pr-3">
              <svg
                key={`${missingClaimCounts.completed}-${missingClaimCounts.pending}-${missingClaimCounts.failed}-${missingClaimCounts.total}`}
                id="donutSvg"
                width="80"
                height="80"
                viewBox="0 0 136 136"
                strokeWidth="6"
              >
                <circle className="bg" cx="68" cy="68" r="65" stroke="#f2f2f2" fill="none"></circle>
                <circle
                  id="seg1"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#0DAC59"
                  fill="none"
                  strokeDasharray={`${billingHoldDonutData.today} ${billingHoldDonutData.circumference - billingHoldDonutData.today}`}
                  strokeDashoffset="0"
                  strokeLinecap="round"
                ></circle>
                <circle
                  id="seg2"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#E4B200"
                  fill="none"
                  strokeDasharray={`${billingHoldDonutData.lessThan7} ${billingHoldDonutData.circumference - billingHoldDonutData.lessThan7}`}
                  strokeDashoffset={-billingHoldDonutData.today}
                  strokeLinecap="round"
                ></circle>
                <circle
                  id="seg3"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#F6447E"
                  fill="none"
                  strokeDasharray={`${billingHoldDonutData.moreThan8} ${billingHoldDonutData.circumference - billingHoldDonutData.moreThan8}`}
                  strokeDashoffset={-(billingHoldDonutData.today + billingHoldDonutData.lessThan7)}
                  strokeLinecap="round"
                ></circle>
                <g className="iconGroup" fill="transparent" transform="translate(50 50) scale(1.2)">
                  <path
                    d="M7.99609 14.666H15.9994"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.99609 10.6645H11.9978"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.99693 22.6694V22.6694C8.89207 22.669 7.9965 21.7734 7.99609 20.6686V20.6686C7.9965 19.5637 8.89207 18.6681 9.99693 18.6677V18.6677C11.1018 18.6681 11.9974 19.5637 11.9978 20.6686V20.6686C11.9974 21.7734 11.1018 22.669 9.99693 22.6694V22.6694Z"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.6669 23.0029H20.0017V28.005H16.6669C16.2986 28.005 16 27.7064 16 27.3381V23.6699C16 23.3015 16.2986 23.0029 16.6669 23.0029Z"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20.6669 17.3337H23.3347C23.7031 17.3337 24.0017 17.6323 24.0017 18.0007V28.0049H20V18.0007C20 17.6323 20.2986 17.3337 20.6669 17.3337Z"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M24.6709 20.335H27.3386C27.707 20.335 28.0056 20.6336 28.0056 21.0019V27.3379C28.0056 27.7062 27.707 28.0048 27.3386 28.0048H24.0039V21.0019C24.0039 20.6336 24.3025 20.335 24.6709 20.335Z"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.9994 28.0049H6.66387C5.1905 28.0049 3.99609 26.8105 3.99609 25.3371V6.66265C3.99609 5.18928 5.1905 3.99487 6.66387 3.99487H17.5638C18.2714 3.99487 18.9499 4.27594 19.4502 4.77625L23.223 8.54905C23.7234 9.04936 24.0044 9.72792 24.0044 10.4355V13.3321"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>
            </div>
            <div className="d-flex flex-column w-100">
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#0DAC59" />
                  </span>
                  Today
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : billingHoldCounts.today}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#E4B200" />
                  </span>
                  &lt; 7 days
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : billingHoldCounts.less_than_7_days}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#F6447E" />
                  </span>
                  &gt; 8 days
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : billingHoldCounts.more_than_8_days}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Orders */}
      <div ref={ordersCardRef} className="col-lg-3 col-md-6 col-12 mb-2 pb-1">
        <div className="w-100 sh_statusIndicatorbox linear_gradient p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="p1-strong mb-2">Orders</h1>
            {canViewOrdersCountEyeIcon && onReviewOrdersClick && (
              <div>
                <a className="btn sh_btniconnew" onClick={() => { const rect = getRectFromRef(ordersCardRef); if (rect) onReviewOrdersClick(rect); }} style={{ cursor: 'pointer' }} title="View Review Completed Orders">
                  <EyeIcon />
                </a>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center">
            <div className=" d-flex justify-content-center pr-3">
              <svg id="donutSvg" width="80" height="80" viewBox="0 0 136 136" strokeWidth="6">
                <circle className="bg" cx="68" cy="68" r="65" stroke="#f2f2f2" fill="none"></circle>
                <circle
                  id="seg1"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#0DAC59"
                  fill="none"
                  strokeDasharray={`${ordersDonutData.completed} ${ordersDonutData.circumference - ordersDonutData.completed}`}
                  strokeDashoffset="0"
                  strokeLinecap="round"
                ></circle>
                <circle
                  id="seg2"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#E4B200"
                  fill="none"
                  strokeDasharray={`${ordersDonutData.pending} ${ordersDonutData.circumference - ordersDonutData.pending}`}
                  strokeDashoffset={-ordersDonutData.completed}
                  strokeLinecap="round"
                ></circle>
                <circle
                  id="seg3"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#F6447E"
                  fill="none"
                  strokeDasharray={`${ordersDonutData.scheduled} ${ordersDonutData.circumference - ordersDonutData.scheduled}`}
                  strokeDashoffset={-(ordersDonutData.completed + ordersDonutData.pending)}
                  strokeLinecap="round"
                ></circle>
                <g className="iconGroup" fill="transparent" transform="translate(50 50) scale(1.2)">
                  <path
                    d="M5.33203 6.66667V25.3333C5.33203 26.8061 6.52594 28 7.9987 28H23.9987C25.4715 28 26.6654 26.8061 26.6654 25.3333V12C26.6654 10.5272 25.4715 9.33333 23.9987 9.33333H7.9987C6.52594 9.33333 5.33203 8.13943 5.33203 6.66667V6.66667C5.33203 5.19391 6.52594 4 7.9987 4H25.332"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21 17.6667C21 17.1145 20.5523 16.6667 20 16.6667H18V14.6667C18 14.1145 17.5523 13.6667 17 13.6667H15C14.4477 13.6667 14 14.1145 14 14.6667V16.6667H12C11.4477 16.6667 11 17.1145 11 17.6667V19.6667C11 20.219 11.4477 20.6667 12 20.6667H14V22.6667C14 23.219 14.4477 23.6667 15 23.6667H17C17.5523 23.6667 18 23.219 18 22.6667V20.6667H20C20.5523 20.6667 21 20.219 21 19.6667V17.6667Z"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>
            </div>
            <div className="d-flex flex-column w-100">
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#F6447E" />
                  </span>
                  Pending
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : ordersCounts.pending}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#E4B200" />
                  </span>
                  Ready to Review
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : ordersCounts.ready_to_review}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#0DAC59" />
                  </span>
                  Completed
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : ordersCounts.completed}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Average Length of Stay 
      <div className="col-lg-3 col-md-6 col-12 mb-2 pb-1">
        <div className="w-100 sh_statusIndicatorbox olivegreen p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="p1-strong mb-2">Average Length of Stay</h1>
          </div>
          <div className="d-flex align-items-center">
            <div className=" d-flex justify-content-center pr-3">
              <svg id="donutSvg" width="80" height="80" viewBox="0 0 136 136" strokeWidth="6">
                <circle className="bg" cx="68" cy="68" r="65" stroke="#f2f2f2" fill="none"></circle>
                <circle
                  id="seg1"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#0DAC59"
                  fill="none"
                  strokeDasharray={`${losDonutData.privateCare} ${losDonutData.circumference - losDonutData.privateCare}`}
                  strokeDashoffset="0"
                  strokeLinecap="round"
                ></circle>
                <circle
                  id="seg2"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#E4B200"
                  fill="none"
                  strokeDasharray={`${losDonutData.workComp} ${losDonutData.circumference - losDonutData.workComp}`}
                  strokeDashoffset={-losDonutData.privateCare}
                  strokeLinecap="round"
                ></circle>
                <circle
                  id="seg3"
                  className="sh_donutsegment"
                  cx="68"
                  cy="68"
                  r="65"
                  stroke="#F6447E"
                  fill="none"
                  strokeDasharray={`${losDonutData.employerServices} ${losDonutData.circumference - losDonutData.employerServices}`}
                  strokeDashoffset={-(losDonutData.privateCare + losDonutData.workComp)}
                  strokeLinecap="round"
                ></circle>
                <g className="iconGroup" fill="transparent" transform="translate(50 50) scale(1.2)">
                  <path
                    d="M27.9161 17.91C26.9005 23.9357 21.5184 28.2355 15.4171 27.8956C9.31582 27.5556 4.44472 22.6845 4.10476 16.5832C3.76481 10.482 8.06462 5.09988 14.0904 4.08423"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M27.3325 11.7302C27.5955 12.4366 27.7906 13.1664 27.9154 13.9098"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M25.3415 8.25949C24.8603 7.6769 24.3248 7.14137 23.7422 6.66016"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.2694 4.66854C19.5631 4.405 18.8333 4.20937 18.0898 4.0843"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21.3376 16.5216H15.4805V9.33057"
                    stroke="#5F6C85"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>
            </div>
            <div className="d-flex flex-column w-100">
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#F6447E" />
                  </span>
                  Employer Services
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : `${averageLos.employer_services}min`}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#E4B200" />
                  </span>
                  Work Comp
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : `${averageLos.work_comp} min`}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#0DAC59" />
                  </span>
                  Private
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : `${averageLos.private} min`}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      */}
      <div ref={missingClaimCardRef} className="col-lg-3 col-md-6 col-12 mb-2 pb-1">
        <div className="w-100 sh_statusIndicatorbox olivegreen p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="p1-strong mb-2">Missing Claim Numbers</h1>
            {canViewMissingClaimNumbersEyeIcon && (
              <div>
                <a
                  className="btn sh_btniconnew"
                  onClick={() => {
                    const rect = getRectFromRef(missingClaimCardRef)
                    if (rect && typeof onMissingClaimNumbersClick === 'function') {
                      onMissingClaimNumbersClick(rect)
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  title="View Missing Claim Numbers"
                >
                  <EyeIcon />
                </a>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center">
            <div className="d-flex justify-content-center pr-3">
              <div className="sh_empdashdonut-wrapper" style={{ position: 'relative', width: '80px', height: '80px' }}>
                <svg id="donutSvg" width="80" height="80" viewBox="0 0 136 136" strokeWidth="6">
                  <circle className="bg" cx="68" cy="68" r="65" stroke="#f2f2f2" fill="none"></circle>
                  <circle
                    id="seg1"
                    className="sh_donutsegment"
                    cx="68"
                    cy="68"
                    r="65"
                    stroke="#0DAC59"
                    fill="none"
                    strokeDasharray={`${missingClaimDonutData.completed} ${missingClaimDonutData.circumference - missingClaimDonutData.completed}`}
                    strokeDashoffset="0"
                    strokeLinecap="round"
                  ></circle>
                  <circle
                    id="seg2"
                    className="sh_donutsegment"
                    cx="68"
                    cy="68"
                    r="65"
                    stroke="#E4B200"
                    fill="none"
                    strokeDasharray={`${missingClaimDonutData.pending} ${missingClaimDonutData.circumference - missingClaimDonutData.pending}`}
                    strokeDashoffset={-missingClaimDonutData.completed}
                    strokeLinecap="round"
                  ></circle>
                  <circle
                    id="seg3"
                    className="sh_donutsegment"
                    cx="68"
                    cy="68"
                    r="65"
                    stroke="#F6447E"
                    fill="none"
                    strokeDasharray={`${missingClaimDonutData.failed} ${missingClaimDonutData.circumference - missingClaimDonutData.failed}`}
                    strokeDashoffset={-(missingClaimDonutData.completed + missingClaimDonutData.pending)}
                    strokeLinecap="round"
                  ></circle>
                </svg>
                <div
                  className="sh_empdashdonut-center"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }}
                >
                  <div className="sh_empdashdonut-card" style={{ textAlign: 'center' }}>
                    <div
                      className="sh_empdashdonut-title"
                      style={{ fontSize: '12px', fontWeight: '500', color: '#5F6C85', marginBottom: '4px' }}
                    >
                      Total
                    </div>
                    <div className="sh_empdashdonut-value" style={{ fontSize: '16px', fontWeight: '700', color: '#1D1917' }}>
                      {loading ? '...' : missingClaimCounts.total}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex flex-column w-100">
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#7D4BC5" />
                  </span>
                  Total
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : missingClaimCounts.total}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#0DAC59" />
                  </span>
                  Completed
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : missingClaimCounts.completed}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#E4B200" />
                  </span>
                  Pending
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : missingClaimCounts.pending}</p>
              </div>
              <div className="d-flex justify-content-between">
                <p className="p3-light text-dark mb-1">
                  <span className="mr-1">
                    <ColorDot color="#F6447E" />
                  </span>
                  Failed
                </p>
                <p className="p3-light text-dark mb-1">{loading ? '...' : missingClaimCounts.failed}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardCounts




